import { useCallback } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org/useActiveOrgId'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress } from '~/types'
import { Card } from '~/types/cards'

type CardsResponse = { cards: Card[] }
type AddCardResponse = { card: Card }
type DeleteCardResponse = { deleted: boolean }

const ERROR_ADD = 'Could not add card'
const ERROR_REMOVE = 'Could not remove card'

export const useCards = () => {
  const activeOrgId = useActiveOrgId()
  const queryClient = useQueryClient()
  const { serverGetRequest, serverPostRequest, serverDeleteRequest } =
    useServerRequests()

  const cardsQuery = useQuery({
    queryKey: [QUERY_KEYS.CARDS, activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const response = await serverGetRequest<CardsResponse>({
        path: '/api/org/cards',
        params: { orgId: activeOrgId ?? '' },
      })
      return response ? response.data.cards : []
    },
  })

  const invalidateCardsList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CARDS] })
  }, [queryClient])

  const addCardMutation = useMutation({
    mutationFn: async (address: IAddress) => {
      const response = await serverPostRequest<AddCardResponse>({
        path: '/api/org/cards',
        params: { orgId: activeOrgId ?? '', address },
        successMessage: 'Card added',
        errorMessage: ERROR_ADD,
      })
      // serverPostRequest swallows network errors and returns void; treat a
      // missing response as a failed mutation so the caller's catch fires.
      if (!response) throw new Error(ERROR_ADD)
      return response.data.card
    },
    onSuccess: (_card, address) => {
      invalidateCardsList()
      // Newly added card has no balances/transfers indexed yet; pre-warm an
      // empty cache and let the BE refetch on next mount.
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CARD_BALANCES, activeOrgId, address],
      })
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CARD_TRANSFERS, activeOrgId, address],
      })
    },
  })

  const removeCardMutation = useMutation({
    mutationFn: async (address: IAddress) => {
      const search = new URLSearchParams({
        orgId: activeOrgId ?? '',
        address,
      })
      const response = await serverDeleteRequest<DeleteCardResponse>({
        path: `/api/org/cards?${search.toString()}`,
        successMessage: 'Card removed',
        errorMessage: ERROR_REMOVE,
      })
      if (!response) throw new Error(ERROR_REMOVE)
      return response.data.deleted
    },
    onSuccess: (_deleted, address) => {
      invalidateCardsList()
      // Drop the removed card's queries entirely — invalidating would trigger
      // a refetch and the BE would 404, surfacing a misleading
      // "Could not load card balances / Card not found" toast on top of the
      // "Card removed" success toast.
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.CARD_BALANCES, activeOrgId, address],
      })
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.CARD_TRANSFERS, activeOrgId, address],
      })
    },
  })

  return {
    cards: cardsQuery.data ?? [],
    isLoading: cardsQuery.isLoading,
    isError: cardsQuery.isError,
    addCard: addCardMutation.mutateAsync,
    isAdding: addCardMutation.isPending,
    removeCard: removeCardMutation.mutateAsync,
    isRemoving: removeCardMutation.isPending,
  }
}
